import { useCallback, useEffect } from 'react';

// A slow, genuine hover-out crosses the panel boundary with very small
// per-event displacement, so the leave event's coordinates can land within a
// pixel or two of the edge -- sub-pixel drift between the rendered box
// (getBoundingClientRect, float) and the reported pointer position makes an
// exact ">=" / "<=" edge comparison unreliable there. Shrinking the "inside"
// zone by this margin keeps near-edge leaves classified as outside (so they
// close) while still catching spurious leaves whose coordinates sit well
// inside the panel (see comment below).
const EDGE_TOLERANCE_PX = 2;

/**
 * Owns all three dismissal triggers for the mega menu panel: `mouseleave`,
 * `Escape`, and click-outside — so "how does this panel close" has one place to
 * look.
 *
 * `mouseleave` is a plain handler returned for the panel root to wire up (no
 * polling, no document-level listener). When it fires, it checks the leave
 * event's coordinates against the panel's own bounding rect (shrunk by
 * `EDGE_TOLERANCE_PX` on each side): if the point is still geometrically inside
 * that margin, the leave is treated as spurious (caused by injected
 * third-party fragment content mutating its own DOM under a stationary cursor)
 * and ignored. If the point is genuinely outside, `onClose` fires immediately —
 * no grace period. A native `mouseleave` can only fire after the cursor was
 * previously within the element, so no separate "armed" tracking is needed.
 *
 * Escape and click-outside are handled via document-level listeners attached
 * only while the panel is mounted and `visible`. Escape also returns focus to
 * the trigger. Click-outside uses the same geometry check as `mouseleave` rather
 * than `el.contains(e.target)` (see the comment inline).
 *
 * @param {object} options
 * @param {React.RefObject<HTMLElement>} options.menuRef - Ref to the panel root.
 * @param {() => void} options.onClose - Called when the panel should dismiss.
 * @param {boolean} [options.visible] - Whether the panel is currently open;
 *   Escape/click-outside listeners are only attached while `true`.
 * @param {React.RefObject<HTMLElement> | null} [options.triggerRef] - Ref to the
 *   trigger element; focus is returned to it on Escape dismissal.
 * @returns {{ onMouseLeave: (e: React.MouseEvent) => void }}
 */
const useMegaMenuDismiss = ({ menuRef, onClose, visible, triggerRef }) => {
	const onMouseLeave = useCallback(
		(e) => {
			const el = menuRef.current;
			if (!el) return;

			const { left, right, top, bottom } = el.getBoundingClientRect();
			const isInside =
				e.clientX >= left + EDGE_TOLERANCE_PX &&
				e.clientX <= right - EDGE_TOLERANCE_PX &&
				e.clientY >= top + EDGE_TOLERANCE_PX &&
				e.clientY <= bottom - EDGE_TOLERANCE_PX;

			if (!isInside) onClose();
		},
		[menuRef, onClose]
	);

	useEffect(() => {
		if (!visible) return undefined;

		const handleKeyDown = (e) => {
			if (e.key === 'Escape') {
				onClose();
				triggerRef?.current?.focus();
			}
		};

		const handleClickOutside = (e) => {
			const el = menuRef.current;
			if (!el) return;

			// Geometry check instead of el.contains(e.target): if a fragment's
			// own click handler (e.g. "show more") replaces/removes the clicked
			// node before this listener runs, e.target is detached and
			// contains() would wrongly report "outside" even though the click
			// was physically inside the panel. Coordinates are unaffected by
			// what the fragment does to its own DOM. Same technique as the
			// mouseleave check above.
			const { left, right, top, bottom } = el.getBoundingClientRect();
			const isInsidePanel =
				e.clientX >= left &&
				e.clientX <= right &&
				e.clientY >= top &&
				e.clientY <= bottom;

			if (isInsidePanel) return;
			if (e.target.closest('.ws-nav-drawer__featured-bar-wrapper'))
				return;

			onClose();
		};

		document.addEventListener('keydown', handleKeyDown);
		document.addEventListener('click', handleClickOutside);
		return () => {
			document.removeEventListener('keydown', handleKeyDown);
			document.removeEventListener('click', handleClickOutside);
		};
	}, [visible, onClose, triggerRef, menuRef]);

	return { onMouseLeave };
};

export default useMegaMenuDismiss;
